import { ColumnDef } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { Button, Stack } from 'react-bootstrap';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { ButtonData } from '../../../../../components/buttonGroup/ButtonGroup';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import ConfirmationModal from '../../../../../components/modals/ConfirmationModal';
import VideoModal from '../../../../../components/modals/VideoModal';
import { LinkCell } from '../../../../../components/tables/cellComponents/LinkCell';
import { FilterField, FilterForm } from '../../../../../components/tables/filterForms/FilterForm';
import SFCTable from '../../../../../components/tables/SFCTable';
import { useToast } from '../../../../../context/ToastContext';
import { useListFiltersInUrl } from '../../../../../hooks';
import {
  useDeleteTraningVideoMutation,
  useGetTrainingVideosQuery
} from '../../../../../services/endpoints/content/trainingVideo';
import { Scopes, User } from '../../../../../services/endpoints/people/user';
import { getCurrentUser } from '../../../../../services/helper';
import { Document } from '../../../../../types/content.type';
import { trainingVideo } from '../../../../../types/trainingVideos.type';
import { getFilterValue } from '../../../../../utils/listFilterSearchParams';
import { getErrorMessage } from '../../../../../utils/utils';
// import { trainingVideosColumns } from '../../../../../utils/consts/columnDefs/trainingVideos';

const defaultScope = 'active';

export const userCanEditCreateArchive_TrainingVideos = (user: User | null) => {
  if (user?.isSuper || user?.isSupremeAdmin || user?.isGlobal) {
    return true;
  }

  return false;
};

export const userCanView_TrainingVideos = (user: User | null) => {
  if (
    user?.isAdmin ||
    user?.isSuper ||
    user?.isSupremeAdmin ||
    user?.isGlobal ||
    user?.isLocationAdmin ||
    user?.isInstructor
  ) {
    return true;
  }

  return false;
};

const DeleteTrainingVideoButton: React.FC<{ id: number }> = ({ id }) => {
  const [showModal, setShowModal] = useState(false);
  const [deleteTrainingVideo, { isLoading }] = useDeleteTraningVideoMutation();
  const { addToast } = useToast();

  const handleDelete = async () => {
    try {
      await deleteTrainingVideo(id).unwrap();
      addToast('Training video deleted successfully', 'success');
      setShowModal(false);
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  return (
    <>
      <button
        className="text-info fw-bolder btn btn-link p-0"
        style={{ fontSize: '.875rem', textDecoration: 'underline' }}
        onClick={() => setShowModal(true)}
      >
        Delete
      </button>
      <ConfirmationModal
        show={showModal}
        title="Delete Training Video"
        message="Are you sure you want to delete this training video?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowModal(false)}
        isLoading={isLoading}
      />
    </>
  );
};

export const TrainingVideosView = () => {
  const { addToast } = useToast();
  const [filters, setFilters] = useListFiltersInUrl({
    scope: defaultScope,
    data: { page: 1, entries: 10 }
  });
  const [scopes, setScopes] = useState<Scopes>({});
  const { data, error, isLoading, isFetching } = useGetTrainingVideosQuery(filters);
  const [trainingVideo, setTrainingVideo] = useState<trainingVideo[]>([]);
  const [buttons, setButtons] = useState<ButtonData[][]>();
  const navigate: NavigateFunction = useNavigate();
  const [modalShow, setModalShow] = useState(false);
  const [urlVideo, setUrlVideo] = useState('');
  const currentUser = getCurrentUser();

  const trainingVideosColumns: ColumnDef<Document>[] = [
    {
      header: 'ID',
      accessorKey: 'id',
      cell: ({ getValue }) => (
        <LinkCell
          content={getValue<number>()}
          url={`/admin/training/videos/${getValue<number>()}`}
        />
      )
    },
    {
      header: 'Name',
      accessorKey: 'name'
    },
    {
      header: 'Category',
      accessorKey: 'category'
    },
    {
      header: 'Code',
      accessorKey: 'code',
      cell: ({ row }) => (
        <Button
          variant="primary"
          onClick={() => {
            setUrlVideo('https://www.youtube.com/embed/' + row.original.code);
            setModalShow(true);
          }}
        >
          View video
        </Button>
      )
    },
    {
      header: () => null,
      id: 'actions',
      enableSorting: false,
      cell: ({ row }) => (
        <Stack
          direction="horizontal"
          gap={2}
        >
          <LinkCell
            content="View"
            url={`/admin/training/videos/${row.original.id}`}
            underline
          />

          {userCanEditCreateArchive_TrainingVideos(currentUser) && (
            <>
              <LinkCell
                content="Edit"
                url={`/admin/training/videos/${row.original.id}/edit`}
                underline
              />
              <DeleteTrainingVideoButton id={row.original.id} />
            </>
          )}
        </Stack>
      )
    }
  ];

  const filterFormFields: FilterField[] = [
    {
      name: 'name',
      label: 'Name',
      value: getFilterValue(filters.data?.filterBy, 'name')
    }
  ];

  useEffect(() => {
    if (!error && !isLoading && data && data.results) {
      if (data.scopes) {
        setScopes(
          data.scopes.reduce((acc: Scopes, curr: Scopes) => ({ ...acc, ...curr }), {} as Scopes)
        );
        setButtons(
          data.scopes.map((sMap) =>
            Object.entries(sMap).map(([scope, title]) => {
              let scope_count = 0;
              if (data.scope_counts != null) {
                scope_count = data.scope_counts[scope] ?? 0;
              }
              return {
                title: `${title} (${scope_count})`,
                key: scope
              };
            })
          )
        );
      }
    } else if (error) {
      addToast('Error while loading data', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data && data.results) {
      setTrainingVideo(data.results);
    }
  }, [data]);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy
      }
    }));
  };

  return (
    <div className="py-2">
      <VideoModal
        show={modalShow}
        onHide={() => setModalShow(false)}
        urlVideo={urlVideo}
      />
      <SFCTable
        name={`${scopes[filters?.scope!] ?? ''} Videos`}
        columns={trainingVideosColumns}
        data={trainingVideo}
        count={data?.count}
        defaultScope={filters.scope ?? defaultScope}
        scopes={buttons}
        isLoading={isLoading}
        isFetching={isFetching}
        filterForm={
          <FilterForm
            fields={filterFormFields}
            onApplyFilters={setFilterBy}
          />
        }
        onFiltersChanged={setFilters}
        newItemEl={
          <SimpleButton
            variant="sfc-blueberry"
            height={40}
            truncate={false}
            onClick={() => navigate('/admin/training/videos/new')}
          >
            New Video
          </SimpleButton>
        }
      />
    </div>
  );
};
